import { T } from 'src/shared/helpers';
import SelectAllIcon from 'src/shared/material_icons/image/filter_center_focus.svg';
import { isRealMesh, materialKey, materialLabel } from './objectEditorConfig';
import { liveTransform, readRotation, rememberLoadedTransform, toRadians } from './transformSpace';
import { notifyMaterialUsageChanged } from './materialUsage';
import SelectionInspector from './selectionInspector';
import { CollapseAllIcon, EmptyNodeIcon, ExpandAllIcon, ObjectsIcon, SingleBranchIcon, VtoIcon } from '../../../../../widgets/icons/scene';
import { ObjectsRailIcon } from './sceneRailIcons';
import { SceneEditor } from '@zakeke/zakeke-3d-core';
import { Dialog, useDialogManager } from '../../../../../widgets/dialog';
import { FormControl } from '../../../../../widgets/formControl';
import { Input } from '../../../../../widgets/input';
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import DAMDialog from 'src/components/pages/preview3D/dialogs/damDialog';
import { UploadDialog } from 'src/components/pages/preview3D/dialogs/uploadDialog';
import useMeshSides from 'src/components/pages/preview3D/hooks/useMeshSide';
import usePreview3DValidation from 'src/components/pages/preview3D/hooks/usePreview3DValidation';
import { Preview3DStoreContext } from 'src/components/widgets/preview3D';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { getInvalidSceneUploadFileSelectionMessage, SUPPORTED_SCENE_FILE_UPLOAD_EXTENSIONS } from 'src/shared/helpers';
import {
    DuplicateIcon as CloneIcon,
    GroupIcon,
    PlusIcon as ImportIcon,
    MeshNodeIcon as ModelIcon,
    TrashIcon as RemoveIcon,
    UploadIcon
} from '../../../../../widgets/icons/scene';
import { TreeComponent, TreeHandle, useTreeNodeManager } from '../tree';
import { useRememberedBranches } from '../treeBranchMemory';
import { useProductConfigurationState } from 'src/components/pages/productConfiguration/state';
import { readMaterialPreview } from './materialPreview';
import ObjectOverlayComponent from './overlay/ObjectOverlayComponent';
import { showErrorNotification } from '../../../../../widgets/notificationToaster';
import SectionPanel from './sectionPanel';
import { useRemovalPrompt } from './confirmRemoval';
import { SceneEditorAction } from './sceneEditorActionBar';
import { applySceneLocks, isSceneNodeLocked, setSceneNodeLocked } from './sceneLocks';

export interface SceneObjectNode {
    id: string;
    name: string;
    position: [number, number, number];
    rotation: [number, number, number];
    scaling: [number, number, number];
    materialOptions: { value: string; label: string; previewUrl?: string }[];
    selectedMaterial: { value: string; label: string };
    showInAR: boolean;
    enableInVTO: boolean;
    children: SceneObjectNode[];
    visible?: boolean;
    /** Bloccata: non si sceglie dal palco e non si trasforma. Vedi `sceneLocks`. */
    locked?: boolean;
    displayOrder?: number;
    metadata?: { [key: string]: any }; // Metadata personalizzati per il nodo
    data: any; // Per ObjectEditor conterrà la mesh di Babylon
    onNodeClick: () => void; // Handler per la selezione mesh quando si clicca sul nodo
}

interface ObjectEditorProps {
    treeRef: React.RefObject<TreeHandle>;
    /**
     * L'elenco dei materiali dello Scene Editor. Qui conta come SEGNALE: quando cambia — un materiale
     * nato, rinominato, tolto — l'elenco degli slot si richiede al motore.
     */
    materials?: any[];
    /** Apre un materiale: la libreria lo sceglie e l'Inspector passa a lui. */
    onOpenMaterial?: (materialKey: string) => void;
    sceneEditor: SceneEditor;
    onMeshUpdate: (meshId: string, updates: Partial<SceneObjectNode>) => void;
    onMeshesUpdated: (meshes: any[]) => void;
    notifySceneEditorUpdates: () => void;
    onRefreshMeshVisibility?: (refreshFn: () => void) => void; // Callback per esporre la funzione di refresh
    onInspectorChange?: (component: React.ReactNode | null) => void;
    /** Porta la colonna di sinistra sui materiali e accende quello dell'oggetto aperto. */
    onRevealMaterial?: (materialId: string) => void;
    /**
     * «DOVE STA?» per l'oggetto aperto. Passa dallo Scene Editor e non da qui, perché il pannello vive
     * più a lungo di questo elenco: quando la freccetta si preme da un'altra sezione, la risposta vuol
     * dire tornare negli oggetti, e qui non c'è più nessuno ad ascoltare.
     */
    onRevealObject?: (rowID: string, asRow?: boolean) => void;
    /**
     * «DOVE STA?» CHIESTO DA UN'ALTRA SEZIONE. Chi lo chiede conosce il `dataID` della cosa — l'id
     * della mesh o del materiale — e non quello della riga, che è una faccenda interna a questo
     * elenco. La traduzione la fa chi possiede l'albero, cioè noi. Con `asRow` invece chi chiede
     * conosce la riga: è la freccetta del pannello di un oggetto.
     */
    whereRequest?: { dataID: string; asRow?: boolean; token: number } | null;
    /** La domanda è arrivata all'albero: chi l'ha fatta la può dimenticare. */
    onWhereServed?: (token: number) => void;
}

// «Un ramo alla volta», per scena: tornando su Objects la lista lo ritrova acceso se lo era.
const singleBranchByScene = new WeakMap<object, boolean>();
const ObjectEditor: FC<ObjectEditorProps> = ({
    treeRef,
    materials: libraryMaterials,
    onOpenMaterial,
    onMeshUpdate,
    onMeshesUpdated,
    sceneEditor,
    notifySceneEditorUpdates,
    onRefreshMeshVisibility,
    onInspectorChange,
    onRevealMaterial,
    onRevealObject,
    whereRequest,
    onWhereServed
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const preview3DStore = useContext(Preview3DStoreContext);
    const validate = usePreview3DValidation();
    const { createMeshSide } = useMeshSides();
    const [zkProduct, designManager, sceneManager, zkScene] = usePreview3DStore((x) => [x.zkProduct, x.designManager, x.sceneManager, x.zkScene]);

    // Stato per forzare il refresh delle mesh
    const [meshRefreshTrigger, setMeshRefreshTrigger] = useState(false);
    const [meshes, setMeshes] = useState<any[]>(() => {
        const initialMeshes = sceneEditor?.getSceneMeshes() || [];
        return initialMeshes;
    });

    useEffect(() => {
        if (meshRefreshTrigger && sceneEditor) {
            const newMeshes = sceneEditor.getSceneMeshes();
            setMeshes(newMeshes);
            setMeshRefreshTrigger(false);
        }
    }, [meshRefreshTrigger, sceneEditor]);

    // Esponi la funzione di refresh tramite il callback
    useEffect(() => {
        if (onRefreshMeshVisibility) {
            const refreshFunction = () => {
                setMeshRefreshTrigger(true);
            };
            onRefreshMeshVisibility(refreshFunction);
        }
    }, [onRefreshMeshVisibility]);

    // Controlla periodicamente i cambiamenti nelle mesh per le VTO reference
    useEffect(() => {
        if (!sceneEditor) return;

        const checkMeshChanges = () => {
            const currentMeshes = sceneEditor.getSceneMeshes();
            const currentMeshCount = currentMeshes.length;
            const currentVTOMeshCount = currentMeshes.filter((mesh) => mesh.name && mesh.name.toLowerCase().includes('vto_reference')).length;

            // Confronta con lo stato precedente
            const previousMeshCount = meshes.length;
            const previousVTOMeshCount = meshes.filter((mesh) => mesh.name && mesh.name.toLowerCase().includes('vto_reference')).length;

            // Se il numero di mesh è cambiato o se le VTO reference sono cambiate
            if (currentMeshCount !== previousMeshCount || currentVTOMeshCount !== previousVTOMeshCount) {
                setMeshRefreshTrigger(true);
            }
        };

        // Controlla ogni 500ms per cambiamenti
        const interval = setInterval(checkMeshChanges, 500);

        return () => clearInterval(interval);
    }, [sceneEditor, meshes]);

    // L'ELENCO DEGLI SLOT SI RILEGGE QUANDO LA LIBRERIA CAMBIA. Si calcolava una volta, alla nascita
    // della sezione: un materiale creato o rinominato dopo non compariva fra quelli da scegliere — e
    // con la libreria nella striscia la sezione degli oggetti non rinasce mai. `libraryMaterials` è il
    // segnale; i materiali si chiedono al motore, che è l'unico a sapere quali esistono.
    const materials = useMemo(
        () => (sceneEditor ? sceneEditor.getAllMaterials() : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sceneEditor, libraryMaterials]
    );

    // Funzione per costruire la gerarchia delle mesh
    const buildMeshHierarchy = useCallback(
        (meshes: any[]): SceneObjectNode[] => {
            if (!meshes || meshes.length === 0) {
                return [];
            }

            const meshMap = new Map<string, any>();
            const rootMeshes: any[] = [];

            // Prima passata: crea la mappa delle mesh
            meshes.forEach((mesh) => {
                const meshId = mesh.uniqueId || mesh.id || `mesh-${Date.now()}-${Math.random()}`;
                meshMap.set(meshId, mesh);
            });

            // Seconda passata: identifica le mesh root
            meshes.forEach((mesh) => {
                const hasParent = mesh.parent && meshMap.has(mesh.parent.uniqueId || mesh.parent.id);
                if (!hasParent) {
                    rootMeshes.push(mesh);
                }
            });

            // L'ELENCO DEI MATERIALI È DELLA SCENA, NON DELLA MESH: sono gli stessi per tutte, e
            // costruirlo dentro la ricorsione voleva dire rifarlo una volta per mesh — con la
            // lettura dell'anteprima ripetuta altrettante volte. Qui si costruisce una volta e lo
            // portano tutte.
            //
            // LA FACCIA DI UN MATERIALE È LA SUA FOTOGRAFIA, quando c'è: fra «Inner.007» e
            // «Other.006» non sceglie nessuno leggendo. Si legge dai metadata della scena — è un
            // accesso a un oggetto, non uno scatto — e chi la fotografia non ce l'ha resta col
            // cerchio vuoto, che è diverso dal non esserci.
            const materialOptions = materials.map((mat: any) => ({
                value: materialKey(mat),
                label: materialLabel(mat),
                previewUrl: readMaterialPreview(sceneEditor, mat.id) ?? undefined
            }));

            // Funzione ricorsiva per convertire mesh in SceneObjectNode
            const convertMeshToNode = (mesh: any): SceneObjectNode => {
                const meshId = mesh.uniqueId || mesh.id || `mesh-${Date.now()}-${Math.random()}`;

                // Trova i figli di questa mesh
                const children = meshes.filter((m) => m.parent && (m.parent.uniqueId === meshId || m.parent.id === meshId)).map(convertMeshToNode);

                const meshMaterials = materialOptions;

                // IL MATERIALE DELLA MESH È IL SUO, O NESSUNO. Prima, quando non si trovava nell'elenco,
                // si prendeva il primo — quasi sempre del primo oggetto importato — e lo slot diceva una
                // cosa falsa: dopo un secondo import gli oggetti nuovi sembravano dipinti col materiale
                // di quello vecchio, e la freccetta portava su quello.
                const selectedMaterial = mesh.material
                    ? (meshMaterials.find((option: any) => option.value === materialKey(mesh.material)) ?? {
                          value: materialKey(mesh.material),
                          label: materialLabel(mesh.material)
                      })
                    : { value: '', label: '' };

                // Determina se è un nodo root (senza parent tra le mesh)
                const isRootNode = !mesh.parent || !meshes.some((m) => m.uniqueId === mesh.parent.uniqueId || m.id === mesh.parent.id);

                // Determina se è un nodo transform (contiene "transform" nel nome)
                const isTransformNode = (mesh.name || '').toLowerCase().includes('transform');

                // Inizializza metadata dal mesh esistente o crea nuovo
                const baseMetadata = mesh.metadata || {};

                // Aggiungi metadata automatici
                const metadata = {
                    ...baseMetadata,
                    displayOrder: baseMetadata.displayOrder || 0,
                    ...(isRootNode && { isRootNode: true }),
                    ...(isTransformNode && { isTransformNode: true })
                };

                // Determina la visibilità del nodo basandosi sul parent se presente
                let meshVisibility: boolean;

                if (mesh.isVisible === false) {
                    // Se la mesh è esplicitamente false, forza a false
                    meshVisibility = false;
                } else if (mesh.parent && mesh.parent.isVisible !== undefined) {
                    // Se ha un parent, usa la visibilità del parent
                    meshVisibility = mesh.parent.isVisible !== false;
                } else {
                    // Se non ha parent, usa la visibilità della mesh stessa
                    meshVisibility = mesh.isVisible !== false;
                }

                // COM'ERA AL CARICAMENTO, prima che chiunque la tocchi: è ciò a cui «Reset transform»
                // la riporta. Si segna una volta sola, e qui è la prima volta che la mesh si vede.
                rememberLoadedTransform(mesh);

                const node: SceneObjectNode = {
                    id: meshId,
                    name: mesh.name || 'Unnamed Mesh',
                    position: mesh.position ? [mesh.position.x, mesh.position.y, mesh.position.z] : [0, 0, 0],
                    rotation: readRotation(mesh),
                    scaling: mesh.scaling ? [mesh.scaling.x, mesh.scaling.y, mesh.scaling.z] : [1, 1, 1],

                    materialOptions: meshMaterials,
                    selectedMaterial,
                    // `|| true` rendeva «Show in AR» acceso SEMPRE, qualunque cosa dicesse la mesh: un
                    // interruttore che non si può spegnere non è un interruttore. Si legge e basta,
                    // come faceva il vecchio backoffice.
                    showInAR: sceneEditor.isAREnabledForMesh(mesh.id),
                    enableInVTO: sceneEditor.isTryOnEnabledForMesh(mesh.id),
                    children,
                    data: mesh,
                    visible: meshVisibility,
                    // IL LUCCHETTO SI LEGGE CON LA CHIAVE CON CUI SI SCRIVE: l'`id` della mesh, quello che
                    // la scena salva (`handleToggleNodeLock`, `onNodeClick`). `meshId` qui è l'`uniqueId`,
                    // un contatore che il motore ridà a ogni caricamento: con quello la mappa non trovava
                    // niente, e ogni ricostruzione dell'elenco riapriva il lucchetto.
                    locked: isSceneNodeLocked(sceneEditor, mesh.id),
                    displayOrder: metadata.displayOrder,
                    metadata, // Aggiungi metadata al nodo
                    // Handler per la selezione mesh quando si clicca sul nodo
                    onNodeClick: () => {
                        if (!sceneEditor) {
                            console.warn('⚠️ SceneEditor not available');
                            return;
                        }

                        try {
                            let targetMesh = mesh;
                            const targetMeshId = targetMesh.id || targetMesh.uniqueId;

                            // Attiva l'highlight
                            if (sceneEditor.setHighlightOver) {
                                sceneEditor.setHighlightOver(true);
                            }

                            // UNA COSA BLOCCATA NON SI PRENDE COL GIZMO. Nel motore scegliere una mesh e
                            // renderla manipolabile sono lo stesso gesto — `selectMesh` fa tutt e due,
                            // e il manico del gizmo si attacca a quello che è stato scelto — quindi per
                            // non darle il gizmo bisogna non sceglierla IN SCENA.
                            //
                            // La riga resta scelta nell'elenco, che è esattamente il posto da cui il
                            // lucchetto dice che si può ancora scegliere. Il prezzo è il contorno in
                            // scena, che una cosa bloccata non prende.
                            if (sceneEditor.selectMesh && !isSceneNodeLocked(sceneEditor, targetMeshId)) {
                                sceneEditor.selectMesh(targetMeshId);
                            }

                            // Attiva la modalità selezione se non è già attiva
                            if (sceneEditor.isSelectionMode !== undefined && !sceneEditor.isSelectionMode) {
                                sceneEditor.isSelectionMode = true;
                            }
                        } catch (error) {
                            console.error('❌ Error selecting mesh from node click:', error);
                        }
                    }
                };

                // Sincronizza anche il metadata sulla mesh di Babylon
                if (!mesh.metadata) mesh.metadata = {};
                Object.assign(mesh.metadata, metadata);

                if (isRootNode || isTransformNode) {
                }

                return node;
            };

            return rootMeshes.map(convertMeshToNode);
        },
        [materials, sceneEditor, meshes]
    );

    // Converti le mesh in SceneObjectNode con logging di riepilogo
    const initialObjects: SceneObjectNode[] = useMemo(() => {
        if (!meshes || meshes.length === 0) {
            return [];
        }

        const result = buildMeshHierarchy(meshes);

        // Riepilogo metadata applicati
        const countMetadata = (nodes: SceneObjectNode[]): { root: number; transform: number; total: number } => {
            let root = 0,
                transform = 0,
                total = 0;

            const count = (nodeList: SceneObjectNode[]) => {
                nodeList.forEach((node) => {
                    total++;
                    if (node.metadata?.isRootNode) root++;
                    if (node.metadata?.isTransformNode) transform++;
                    if (node.children?.length) count(node.children);
                });
            };

            count(nodes);
            return { root, transform, total };
        };

        const stats = countMetadata(result);

        return result;
    }, [meshes, buildMeshHierarchy]);

    const syncNodeToMeshRef = useRef<((node: SceneObjectNode, changed?: Record<string, unknown>) => void) | undefined>(undefined);

    useEffect(() => {
        syncNodeToMeshRef.current = (node: SceneObjectNode, changed?: Record<string, unknown>) => {
            if (!node.data) return;

            const mesh = node.data;

            // SI SCRIVE SOLO CIÒ CHE È STATO TOCCATO, e non è una micro-ottimizzazione: è la
            // differenza fra spostare una cosa e rimetterne a posto altre due. Questa funzione
            // riversava sulla mesh tutti e nove i numeri presi dall’albero, e l’albero non sa nulla di
            // quello che il motore fa per conto suo — il gizmo, l’auto-scala del VTO che porta il
            // modello alla misura del piede di riferimento. Bastava poi ritoccare UN
            // campo qualunque nel pannello perché scala e posizione tornassero a quelle del
            // caricamento: il modello scattava via dalla scena e sembrava sparito.
            //
            // Chi chiama dice che cosa ha cambiato. Senza quella lista si riscrive tutto, come prima.
            const touched = (property: string) => !changed || property in changed;

            // Sincronizza nome
            mesh.name = node.name;

            // Sincronizza posizione
            if (mesh.position && touched('position')) {
                mesh.position.x = node.position[0];
                mesh.position.y = node.position[1];
                mesh.position.z = node.position[2];
            }

            // Sincronizza rotazione
            if (mesh.rotation && touched('rotation')) {
                // IL QUATERNIONE, FINCHÉ C’È, VINCE: si toglie, e da qui comanda la terna — che è quella
                // che il pannello scrive. L’orientamento non salta, perché i gradi mostrati sono stati
                // letti da lui.
                if (mesh.rotationQuaternion) mesh.rotationQuaternion = null;

                mesh.rotation.x = toRadians(node.rotation[0]);
                mesh.rotation.y = toRadians(node.rotation[1]);
                mesh.rotation.z = toRadians(node.rotation[2]);
            }

            // Sincronizza scala
            if (mesh.scaling && touched('scaling')) {
                mesh.scaling.x = node.scaling[0];
                mesh.scaling.y = node.scaling[1];
                mesh.scaling.z = node.scaling[2];
            }

            // IL MATERIALE NON SI RISCRIVE DA QUI. Questo passaggio scatta a ogni campo toccato —
            // posizione, nome, AR — e rimetteva sulla mesh il materiale del nodo, che per una mesh non
            // trovata nell'elenco era il primo della scena: spostare un oggetto lo ridipingeva. A
            // cambiare il materiale è il gesto che lo cambia, cioè lo slot (`'selectedMaterial' in
            // updates`, qui sotto in `onPropertiesUpdate`).

            // LA VISIBILITÀ NON PASSA DI QUI (QA, 2026-10-12). La scrive l'occhio con `showHideMesh`, che tocca
            // `isVisible` — lo stesso campo delle azioni show/hide del Composer. Riscriverla qui, a ogni campo
            // toccato, spegneva con `setEnabled(false)` la mesh nascosta: riaccesa dall'occhio restava spenta,
            // e salvata così in vetrina nessuna opzione la riaccendeva più.

            // Sincronizza metadata
            if (!mesh.metadata) mesh.metadata = {};
            mesh.metadata.displayOrder = node.displayOrder;

            // Sincronizza metadata personalizzati
            if (node.metadata) {
                Object.assign(mesh.metadata, node.metadata);
            }

            // AR e VTO NON si scrivono qui: li scrive il gesto che li cambia, in `objectEditorConfig`.
            // Passare di qui vorrebbe dire riscriverli a ogni spostamento di un nodo, e i metodi del
            // motore scendono nei figli — una modifica qualunque ristamperebbe la scelta su tutta la
            // discendenza.

            if (onMeshUpdate) {
                onMeshUpdate(node.id, node);
            }

            // E ADESSO SI RIDISEGNA. Il palco non dipinge di continuo: disegna quando qualcuno
            // dichiara che c'è qualcosa di nuovo da vedere — è così che una scena 3D ferma non
            // consuma la batteria. Scrivere `mesh.position` cambia il modello e non la fotografia:
            // finché nessuno chiede il fotogramma, a schermo resta quello di prima, e la modifica
            // compariva solo alla mossa successiva — che la faceva chiedere per conto suo. Da fuori
            // era «tiro lo slider e non succede niente, poi tutto insieme»: rotto, ma in ritardo.
            //
            // È lo stesso gesto che fanno già le luci (`render`) e il VTO (`setRenderingNeeded`).
            sceneEditor?.setRenderingNeeded?.();
        };
    }, [onMeshUpdate, sceneEditor]);

    // «DOVE STA?» — la freccetta della testata non cambia niente: porta la riga in vista e la fa
    // lampeggiare. Passa di qui e non dal manico dell'albero perché quel manico è uno solo per
    // quattro sezioni: una chiamata imperativa poteva finire su un altro albero e non fare niente,
    // in silenzio. Il gettone serve a poter chiedere due volte la stessa riga.
    const [blinkRequest, setBlinkRequest] = useState<{ nodeId?: string; dataID?: string; token: number } | null>(null);

    // La domanda della freccetta la riceve lo Scene Editor: il pannello che la porta sopravvive a
    // questo componente, e la funzione si rilegge qui a ogni clic invece di restare quella di quando
    // il pannello è nato. Lo stesso per «apri» — il doppio clic sullo slot.
    const revealObjectRef = useRef(onRevealObject);
    revealObjectRef.current = onRevealObject;
    const openMaterialRef = useRef(onOpenMaterial);
    openMaterialRef.current = onOpenMaterial;

    // SOSTITUIRE UN MATERIALE SOTTO UN EMPTY, solo dove c'era quello: le mesh del ramo che portano
    // `fromKey` prendono `toKey`, le altre restano come sono. Senza `toKey` si svuotano, come lo slot
    // di una mesh singola: Babylon le disegna col materiale neutro, e i loro slot si leggono vuoti
    // (QA, confermato 2026-10-15). Una mesh con più materiali cambia solo le parti che lo portavano.
    const replaceInheritedRef = useRef<(node: SceneObjectNode, fromKey: string, toKey: string) => void>(() => {});
    replaceInheritedRef.current = (node, fromKey, toKey) => {
        const root = node.data;
        if (!root || !sceneEditor) return;

        const target = toKey ? (sceneEditor.scene?.getMaterialByUniqueID?.(Number(toKey)) ?? null) : null;
        if (toKey && !target) return;

        const meshes: any[] = typeof root.getChildMeshes === 'function' ? root.getChildMeshes(false) : [];
        meshes.forEach((mesh) => {
            const material = mesh.material;
            if (!material) return;

            if (material instanceof BABYLON.MultiMaterial) {
                (mesh.subMeshes ?? []).forEach((subMesh: any) => {
                    const part = material.subMaterials[subMesh.materialIndex];
                    if (!part || materialKey(part) !== fromKey) return;

                    if (target) sceneEditor.applyMaterialToSubMesh(target, mesh, subMesh);
                    else material.subMaterials[subMesh.materialIndex] = null;
                });
                return;
            }

            if (materialKey(material) === fromKey) mesh.material = target;
        });

        notifyMaterialUsageChanged();
        notifySceneEditorUpdates?.();
        // È una modifica della scena come le altre: chi tiene il «da salvare» lo sappia.
        onMeshUpdate?.(node.id, node);
    };

    const createObjectOverlayById = useCallback(
        (nodeId: string): (() => React.ReactElement) => {
            return () => {
                // Trova il nodo aggiornato al momento del render dell'overlay
                const { findNodeById } = treeNodeManagerRef.current || {};
                const node = findNodeById ? findNodeById(nodeId) : null;

                if (!node) {
                    console.error('Nodo non trovato per overlay:', nodeId);
                    return <div>Node not found</div>;
                }

                return (
                    <ObjectOverlayComponent
                        key={`overlay-component-${node.id}`}
                        node={node.data ? { ...node, ...liveTransform(node.data) } : node}
                        sceneEditor={sceneEditor}
                        onRevealMaterial={onRevealMaterial}
                        onOpenMaterial={(key) => openMaterialRef.current?.(key)}
                        onReplaceInherited={(fromKey, toKey) => replaceInheritedRef.current(node, fromKey, toKey)}
                        // LA RIGA, NON LA MESH: in un modello importato due mesh possono portare lo stesso
                        // `id`, due righe no.
                        onReveal={() => revealObjectRef.current?.(node.id, true)}
                        onPropertiesUpdate={(updates) => {
                            const { updateNode, findNodeById } = treeNodeManagerRef.current || {};

                            if (!updateNode || !findNodeById) return;

                            // LE TRASFORMATE NON PASSANO DALL'ALBERO (QA, 2026-10-15 — «Basic properties lente»).
                            // I tre numeri si leggono dalla mesh (`liveTransform`), mai dalla copia nella riga, e il
                            // pannello tiene i suoi nello stato locale. Scriverli anche nell'albero non serviva a
                            // nessuno e costava la pagina intera: un albero nuovo ripubblica il pannello, e lo Scene
                            // Editor ridisegna tutto per consegnarlo — a ogni numero, e a ogni passo di un
                            // trascinamento sul campo.
                            const onlyTransform = Object.keys(updates).every((key) => key === 'position' || key === 'rotation' || key === 'scaling');

                            // Aggiorna il nodo nel tree
                            if (!onlyTransform) updateNode(node.id, updates);

                            // Trova il nodo aggiornato e sincronizza con Babylon
                            const updatedNode = findNodeById(node.id);
                            if (updatedNode && syncNodeToMeshRef.current) {
                                const nodeWithUpdates = { ...updatedNode, ...updates };
                                syncNodeToMeshRef.current(nodeWithUpdates, updates as Record<string, unknown>);
                            }

                            // Gestione speciale per i materiali
                            if ('selectedMaterial' in updates && sceneEditor) {
                                // SVUOTARE LO SLOT E TOGLIERE L'ASSEGNAZIONE, non cancellare il materiale:
                                // quello resta nella sua libreria e su ogni altra mesh che lo porta. Qui la
                                // mesh smette di averne uno, lei e i suoi figli.
                                if (!updates.selectedMaterial?.value) {
                                    const clearRecursively = (meshNode: SceneObjectNode) => {
                                        if (meshNode.data) meshNode.data.material = null;
                                        meshNode.children?.forEach(clearRecursively);
                                    };

                                    const toClear = findNodeById(node.id);
                                    if (toClear) clearRecursively(toClear);
                                    notifyMaterialUsageChanged();
                                    notifySceneEditorUpdates();
                                    return;
                                }

                                // La chiave dello slot è l'`uniqueId`: con l'`id` due materiali omonimi
                                // erano lo stesso, e vinceva il primo trovato.
                                const material = sceneEditor.scene?.getMaterialByUniqueID?.(Number(updates.selectedMaterial!.value)) ?? null;
                                if (material) {
                                    // Funzione ricorsiva per applicare il materiale a mesh e figli
                                    const applyMaterialRecursively = (meshNode: SceneObjectNode) => {
                                        sceneEditor.applyMaterial(material, meshNode.data);
                                        meshNode.children?.forEach(applyMaterialRecursively);
                                    };

                                    const currentNodeForMaterial = findNodeById(node.id);
                                    if (currentNodeForMaterial) {
                                        applyMaterialRecursively(currentNodeForMaterial);
                                    }
                                    notifyMaterialUsageChanged();
                                }

                                // Aggiorna anche i nodi figli con il nuovo selectedMaterial
                                const updateChildrenMaterial = (meshNode: SceneObjectNode) => {
                                    meshNode.children?.forEach((child) => {
                                        updateNode(child.id, { selectedMaterial: updates.selectedMaterial });
                                        updateChildrenMaterial(child);
                                    });
                                };

                                const currentNodeForChildren = findNodeById(node.id);
                                if (currentNodeForChildren) {
                                    updateChildrenMaterial(currentNodeForChildren);
                                }
                            }
                        }}
                        onPropertyChanged={(property, value) => {
                            // Notifica gli aggiornamenti del scene editor se necessario
                            if (notifySceneEditorUpdates) {
                                notifySceneEditorUpdates();
                            }
                        }}
                    />
                );
            };
        },
        [meshes, materials]
    );

    // Wrapper per compatibilità con l'hook che si aspetta (node: T) => () => React.ReactNode
    const createObjectOverlay = useCallback(
        (node: SceneObjectNode): (() => React.ReactElement) => {
            // Semplicemente delega alla versione che prende l'ID
            return createObjectOverlayById(node.id);
        },
        [createObjectOverlayById]
    );

    // Ref per memorizzare le funzioni dell'hook dopo l'inizializzazione
    const treeNodeManagerRef = useRef<
        | {
              updateNode: (id: string, updates: Partial<SceneObjectNode>) => void;
              findNodeById: (id: string) => SceneObjectNode | undefined;
          }
        | undefined
    >(undefined);

    // Usa l'hook per la gestione dei nodi con il createObjectOverlay già definito
    const { filter, setFilter, selectedIds, filteredData, updateNode, findNodeById, removeNodes, cloneNodes, treeState, syncInitialVisibilityFromMeshes } =
        useTreeNodeManager<SceneObjectNode>(initialObjects, createObjectOverlay);

    // IL RIFERIMENTO SI AGGIORNA DURANTE IL RENDER, non dopo. Il pannello di un oggetto non
    // tiene dentro il nodo: lo ricerca da qui ogni volta che lo si disegna. Aggiornato dentro un
    // effetto restava indietro di un giro, e gli effetti del figlio girano prima di quelli del
    // padre: l'albero chiedeva un pannello nuovo e se lo vedeva costruire ancora sui dati
    // vecchi, cioè col nome di prima.
    treeNodeManagerRef.current = {
        updateNode,
        findNodeById
    };

    // 🔧 NUOVO: Sincronizza la visibilità iniziale delle mesh solo UNA volta quando i nodi sono pronti
    const [initialSyncDone, setInitialSyncDone] = useState(false);

    useEffect(() => {
        if (initialObjects.length > 0 && !initialSyncDone) {
            syncInitialVisibilityFromMeshes();
            setInitialSyncDone(true);
        }
    }, [initialObjects, syncInitialVisibilityFromMeshes, initialSyncDone]);

    const prepareModel = (meshes: BABYLON.AbstractMesh[]) => {
        const { amountOfSides, amountOfMeshesWithGeometry } = validate();

        if (amountOfSides === 1 && amountOfMeshesWithGeometry === 1) {
            const firstMeshWithGeometry = sceneEditor!.scene!.meshes.find((x) => x.getTotalIndices() > 0);
            if (firstMeshWithGeometry) {
                const meshSide = createMeshSide(zkProduct!.get('colors').at(0).get('colorID'), zkProduct!.get('colors').at(0).get('sides').at(0).get('sideID'));
                meshSide.set('meshID', firstMeshWithGeometry.id);
            }
        }

        // Aggiorna le mesh nell'ObjectEditor dopo l'upload
        if (sceneEditor) {
            setMeshRefreshTrigger(true);
            const updatedMeshes = sceneEditor.getSceneMeshes();
            onMeshesUpdated(updatedMeshes);
        }
    };

    const handleUploadClick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = SUPPORTED_SCENE_FILE_UPLOAD_EXTENSIONS;
        input.multiple = true;

        input.onchange = (e) => {
            document.body.removeChild(input);
            const files = (e.target as HTMLInputElement).files;
            if (files) {
                const validationMessage = getInvalidSceneUploadFileSelectionMessage(files);
                if (validationMessage) {
                    showErrorNotification({ message: validationMessage });
                    return;
                }

                showDialog(
                    'upload-file',
                    <Preview3DStoreContext.Provider value={preview3DStore}>
                        <UploadDialog
                            files={files}
                            onComplete={(meshes) => {
                                prepareModel(meshes);
                                closeDialog('upload-file');
                            }}
                        />
                    </Preview3DStoreContext.Provider>
                );
            }
        };

        input.oncancel = () => {
            document.body.removeChild(input);
        };

        input.style.display = 'none';
        input.click();
        document.body.appendChild(input);
    };

    const handleUploadFromDAMClick = () => {
        showDialog(
            'dam-dialog',
            <Preview3DStoreContext.Provider value={preview3DStore}>
                <DAMDialog
                    onClose={(productAssetUrl: string) => {
                        closeDialog('dam-dialog');
                        showDialog(
                            'upload-file',
                            <Preview3DStoreContext.Provider value={preview3DStore}>
                                <UploadDialog
                                    damProductAssetUrl={productAssetUrl}
                                    onComplete={(meshes) => {
                                        prepareModel(meshes);
                                        closeDialog('upload-file');
                                    }}
                                />
                            </Preview3DStoreContext.Provider>
                        );
                    }}
                />
            </Preview3DStoreContext.Provider>
        );
    };

    // ARRIVA LO STATO VOLUTO, NON QUELLO ATTUALE. L'albero chiama con la visibilità che
    // l'elemento deve AVERE dopo il clic; qui la si negava di nuovo, e in 3D la mesh faceva
    // l'opposto di quello che diceva l'occhio.
    //
    // L'albero ha già aggiornato il proprio stato prima di chiamare: qui si tocca solo
    // Babylon. Aggiornarlo di nuovo — `updateNode` più `toggleVisibility` — era un secondo
    // ribaltamento che annullava il primo.
    const handleToggleNodeVisibility = useCallback(
        (nodeId: string, nextVisible: boolean) => {
            if (!sceneEditor) return;

            // L'albero ragiona per id di nodo, Babylon per id di mesh: i due non coincidono.
            const targetNode = findNodeById(nodeId);
            const meshId = targetNode?.data?.id ?? nodeId;

            // ARRIVA SEMPRE UNA RIGA SOLA, mai un contenitore: l'occhio di un gruppo spegne i suoi
            // figli uno per uno, perché la visibilità di un gruppo è il riassunto di ciò che
            // contiene e non un suo attributo (`TreeComponent`, `groupVisibility`).
            //
            // RIACCENDERE È ANCHE RIABILITARE: una mesh nascosta e poi toccata dall'Inspector veniva
            // spenta con `setEnabled(false)` (vedi `syncNodeToMesh`), e l'occhio da solo non la riportava.
            if (nextVisible) targetNode?.data?.setEnabled?.(true);
            sceneEditor.showHideMesh(meshId, nextVisible, false);
        },
        [sceneEditor, findNodeById]
    );

    // BLOCCARE È UN GESTO SULLA COSA, non sulla riga: lo stato sta nei metadata della scena e da lì
    // torna anche dopo un ricaricamento. Alla mesh si toglie la pescabilità, che è il blocco vero —
    // il raccoglitore di clic di Babylon la salta, quindi non arriva la selezione e con essa nemmeno
    // il gizmo.
    const handleToggleNodeLock = useCallback(
        (nodeId: string, nextLocked: boolean) => {
            const targetNode = findNodeById(nodeId);
            const meshId = targetNode?.data?.id ?? nodeId;

            setSceneNodeLocked(sceneEditor, meshId, nextLocked);
            updateNode(nodeId, { locked: nextLocked });

            // TOGLIERE LA PESCABILITÀ FERMA I CLIC NUOVI, non quello che è già in mano: se il gizmo
            // era attaccato a questa mesh continuava a deformarla proprio mentre si chiedeva che non
            // si potesse. Deselezionarla in scena stacca il manico — è l'unica strada pubblica, e
            // lascia comunque la riga scelta nel suo elenco.
            if (nextLocked) sceneEditor?.selectUnselectMesh?.(meshId, false);
        },
        [sceneEditor, findNodeById, updateNode]
    );

    // Trasforma i nodi in formato compatibile con TreeComponent
    const transformNodesForTree = useCallback(
        (nodes: SceneObjectNode[]): any[] => {
            if (!nodes || nodes.length === 0) {
                return [];
            }

            return nodes.map((node) => ({
                id: node.id,
                name: node.name,
                visible: node.visible,
                // SI RICOPIA CAMPO PER CAMPO, quindi ogni stato nuovo va aggiunto anche qui: senza,
                // il pannello lo vedeva (legge il nodo vero) e la riga no — l'icona restava aperta e
                // spenta, e premendola si richiedeva sempre di bloccare invece di sbloccare.
                locked: node.locked,
                displayOrder: node.displayOrder,
                // COSA È IL NODO, non se ha figli. L'albero sceglieva il glifo da
                // «ha figli», e così una mesh vera con dei figli prendeva il ramo mentre un
                // nodo di trasformazione senza figli prendeva il cubo: il segno diceva la
                // posizione nell'albero invece della natura della cosa.
                // La geometria è il discrimine: una mesh vera ha vertici, un nodo di
                // trasformazione è un'origine e basta. Il nome e i metadata sono il ripiego
                // per quando il modello di Babylon non è a portata.
                kind: isRealMesh(node) ? 'mesh' : 'group',
                children: node.children ? transformNodesForTree(node.children) : [],
                component: createObjectOverlay(node), // Usa il wrapper che prende il nodo
                doNotOpenOverlay: node.name && node.name.toLowerCase().includes('vto_reference'), // Don't open overlay for VTO reference meshes
                customIcon: node.name && node.name.toLowerCase().includes('vto_reference') ? <VtoIcon /> : undefined, // Custom icon for VTO reference meshes
                data: node.data,
                onNodeClick: node.onNodeClick // ✅ AGGIUNTO: Passa l'onNodeClick al nodo trasformato
            }));
        },
        [createObjectOverlay]
    );

    // 5. Ottimizza la memoizzazione dei treeNodes
    // Dopo un caricamento i metadata tornano dal modello, le mesh no: nascono tutte pescabili.
    useEffect(() => {
        applySceneLocks(sceneEditor);
    }, [sceneEditor, treeState.nodes]);

    const treeNodes = useMemo(() => {
        // Usa filteredData se c'è un filtro, altrimenti usa treeState.nodes
        const sourceNodes = filter ? filteredData : treeState.nodes;
        const result = transformNodesForTree(sourceNodes);
        return result;
    }, [treeState.nodes, filteredData, filter, transformNodesForTree]);

    // ✅ NUOVO: Funzione per trovare un nodo basandosi sui dati della mesh
    const findNodeByDataID = useCallback((nodes: SceneObjectNode[], dataID: string): SceneObjectNode | null => {
        for (const node of nodes) {
            // Controlla se questo nodo corrisponde
            if (node.data?.uniqueId === dataID || node.data?.id === dataID || node.id === dataID) {
                return node;
            }
            // Cerca ricorsivamente nei figli
            if (node.children && node.children.length > 0) {
                const found = findNodeByDataID(node.children, dataID);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }, []);

    // Override del treeState per fornire i nodi trasformati
    const treeStateForComponent = useMemo(() => {
        const result = {
            ...treeState,
            nodes: treeNodes,
            findNodeByDataID: (nodes: any[], dataID: string) => findNodeByDataID(nodes, dataID)
        };
        return result;
    }, [treeState, treeNodes, findNodeByDataID]);

    // «DOVE STA?» CHIESTO DA FUORI: si gira all'albero così com'è arrivato. La traduzione da `dataID`
    // a riga la fa lui, che è l'unico a sapere quando il suo elenco è pronto.
    //
    // E SI DICE CHE È ARRIVATA: a una domanda si risponde una volta. Restando in piedi, ogni volta che
    // questa sezione rinasceva la ritrovava, e la riga di allora rilampeggiava riaprendo i suoi rami.
    useEffect(() => {
        if (!whereRequest) return;

        const target = whereRequest.asRow ? { nodeId: whereRequest.dataID } : { dataID: whereRequest.dataID };
        setBlinkRequest((previous) => ({ ...target, token: (previous?.token ?? 0) + 1 }));
        onWhereServed?.(whereRequest.token);
    }, [whereRequest, onWhereServed]);

    // Struttura dati standard per nuovi oggetti
    // «Seleziona tutto» sta fra i comandi, non sopra l'albero. Come casella era l'unico
    // controllo di quel genere nella colonna e sfalsava l'allineamento con le altre sezioni;
    // qui è un comando fra comandi. E siccome accende e spegne, resta premuto finché tutto
    // il visibile è selezionato.
    const selectableIds: string[] = treeState.flatVisibleList.map((node: any) => node.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => treeState.selectedIds.has(id));

    // NELLA BARRA RESTA CIÒ CHE NON HA UN SOGGETTO. «Add object» porta dentro qualcosa che ancora
    // non esiste e «Select all» agisce sull'elenco intero: nessuno dei due appartiene a una riga.
    // Tutti gli altri sono scesi sulla riga, nel suo «…», dove il soggetto è la riga stessa —
    // prima bisognava sceglierla e poi risalire alla barra, con il comando lontano dalla cosa.
    // Una sola conferma per togliere, in tutte le liste: il testo lo compone lei.
    const askRemove = useRemovalPrompt();

    // ESPANDI E COMPRIMI TUTTO (prototipo): un modello arriva con rami annidati, e aprirli a mano uno
    // per uno era il primo gesto di ogni sessione. Un bottone solo: con almeno un ramo aperto chiude
    // tutto, altrimenti apre tutto — e il tooltip dice quale delle due farà.
    const branchIds = useMemo(() => {
        const ids: string[] = [];
        const walk = (list: any[]) =>
            list.forEach((node) => {
                if (node.children?.length) {
                    ids.push(node.id);
                    walk(node.children);
                }
            });
        walk(treeState.nodes);
        return ids;
    }, [treeState.nodes]);
    const anyBranchOpen = branchIds.some((id) => treeState.openNodes.has(id));

    // LA LISTA PARTE APERTA (utente, 2026-10-14) E RICORDA I RAMI CHIUSI fra un accesso e l'altro (utente,
    // 2026-10-16; `treeBranchMemory`). Un ramo si riconosce dall'`id` salvato della sua mesh: quello della riga
    // è l'`uniqueId` del motore, che cambia a ogni caricamento.
    const { state: productState } = useProductConfigurationState();
    useRememberedBranches({
        scope: productState.product.id ? `scene-objects:${productState.product.id}` : null,
        branchIds,
        open: treeState.openNodes,
        setOpen: treeState.setOpenNodes,
        keyOf: (id) => String(findNodeById(id)?.data?.id ?? id)
    });

    // «UN RAMO ALLA VOLTA» TORNA COM'ERA quando la sezione si rimonta: acceso, chiude tutto.
    const branchesOpened = useRef(false);
    useEffect(() => {
        if (branchesOpened.current || !branchIds.length) return;
        branchesOpened.current = true;
        if (sceneEditor && singleBranchByScene.get(sceneEditor)) treeState.setSingleBranch(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branchIds]);

    useEffect(() => {
        if (sceneEditor && branchesOpened.current) singleBranchByScene.set(sceneEditor, treeState.singleBranch);
    }, [sceneEditor, treeState.singleBranch]);

    const tools: SceneEditorAction[] = [
        {
            // «Add» e basta: di che cosa, lo dice già il rail (prototipo; utente, 2026-10-14).
            name: T._('Add', 'Admin'),
            tooltip: T._('A 3D model into this scene', 'Admin'),
            icon: <ImportIcon />,
            kind: 'add',
            // DA DOVE ARRIVA IL MODELLO LO DICE CHI CLICCA, e lo dice sul posto. Prima lo chiedeva
            // una finestra — «Model Upload source», due bottoni e una domanda — cioè un modulo da
            // compilare prima che la cosa esista: due clic e un cambio di contesto per una scelta
            // fra due. Qui è un menu sotto il bottone, e i due modi restano gli stessi di prima.
            //
            // `onClick` non viene mai chiamato finché il menu ha voci: è il ripiego se restasse
            // vuoto.
            onClick: () => handleUploadClick(),
            menu: [
                {
                    name: T._('Upload 3D Model', 'Admin'),
                    tooltip: T._('From a file on this computer', 'Admin'),
                    icon: <UploadIcon />,
                    onClick: handleUploadClick
                },
                {
                    name: T._('Import from 3D DAM', 'Admin'),
                    tooltip: T._('From the asset library', 'Admin'),
                    icon: <ModelIcon />,
                    onClick: handleUploadFromDAMClick
                }
            ]
        },
        {
            // UN RAMO ALLA VOLTA (prototipo; utente, 2026-10-15): acceso, aprire un ramo chiude gli altri.
            // Con tutto chiuso «Expand all» si spegne: aprirli tutti è proprio ciò che questa modalità esclude.
            name: T._('One branch at a time', 'Admin'),
            tooltip: treeState.singleBranch ? T._('Opening a branch closes the others', 'Admin') : T._('Branches open freely', 'Admin'),
            icon: <SingleBranchIcon />,
            kind: 'action',
            active: treeState.singleBranch,
            disabled: branchIds.length === 0,
            onClick: () => treeState.setSingleBranch(!treeState.singleBranch)
        },
        {
            name: anyBranchOpen ? T._('Collapse all', 'Admin') : T._('Expand all', 'Admin'),
            tooltip:
                treeState.singleBranch && !anyBranchOpen
                    ? T._('One branch at a time is on: open a single branch from its arrow', 'Admin')
                    : anyBranchOpen
                      ? T._('Close every branch of the list', 'Admin')
                      : T._('Open every branch of the list', 'Admin'),
            icon: anyBranchOpen ? <CollapseAllIcon /> : <ExpandAllIcon />,
            kind: 'action',
            disabled: branchIds.length === 0 || (treeState.singleBranch && !anyBranchOpen),
            onClick: () => treeState.setOpenNodes(new Set(anyBranchOpen ? [] : branchIds))
        },
        {
            name: allSelected ? T._('Deselect all', 'Admin') : T._('Select all', 'Admin'),
            tooltip: T._('Everything visible in this list', 'Admin'),
            icon: <SelectAllIcon />,
            kind: 'action',
            active: allSelected,
            disabled: selectableIds.length === 0,
            onClick: () => treeState.setSelectedIds(new Set(allSelected ? [] : selectableIds))
        }
    ];

    // UN MOTORE, DUE PORTE. Gli stessi comandi arrivano dal «…» di una riga oppure dal pannello del
    // mucchio. Chi chiama passa l'elenco: il «…» di una riga scelta insieme ad altre passa tutta la
    // selezione, quello di una riga NON scelta la riga sola (`TreeComponent`, `subjectIds`). Qui
    // dentro la selezione non si guarda mai.
    //
    // «Rename» compare solo quando il soggetto è uno: dare un nome solo a otto oggetti insieme non
    // vuol dire niente.
    const subjectCommands = useCallback(
        (ids: string[]): SceneEditorAction[] => {
            if (!ids.length) return [];

            const commands: SceneEditorAction[] = [
                {
                    name: T._('Group', 'Admin'),
                    tooltip: T._('In a new Empty', 'Admin'),
                    icon: <GroupIcon />,
                    kind: 'action',
                    onClick: () => {
                        treeState.closeOverlay();
                        const formData = { name: '' };

                        showDialog(
                            'group-dialog',
                            <Dialog
                                title={T._('New group', 'Admin')}
                                size='small'
                                buttons={[
                                    {
                                        label: T._('Create', 'Admin'),
                                        onClick: () => {
                                            const val = formData.name.trim();
                                            if (!val) return;

                                            // LA CARTELLA È UN EMPTY VERO, non una riga in più nell'albero. Prima
                                            // il gruppo nasceva solo qui dentro: l'albero però si ricostruisce dalle
                                            // mesh della scena a ogni aggiornamento — basta un duplica, un import, un
                                            // remove — e una cartella che in Babylon non esiste sparisce senza dire
                                            // niente. `createGroup` crea il nodo di trasformazione che la regge, e da
                                            // lì in poi la cartella c'è anche dopo il salvataggio.
                                            const meshesToGroup = ids.map((id) => findNodeById(id)?.data).filter(Boolean);

                                            if (!meshesToGroup.length || !sceneEditor?.createGroup) {
                                                closeDialog('group-dialog');
                                                return;
                                            }

                                            // Il gruppo nasce dove stanno le cose: se hanno tutte lo stesso padre,
                                            // dentro quello; se vengono da rami diversi, alla radice.
                                            const parents = new Set<string | null>(meshesToGroup.map((mesh: any) => mesh.parent?.id ?? null));
                                            const parentId = parents.size === 1 ? [...parents][0] : null;

                                            // I PEZZI RESTANO DOVE SONO (QA T2, 2026-10-13): senza l'ultimo argomento
                                            // prendevano il gruppo come padre coi valori di prima, e quelli di un altro
                                            // modello perdevano la sua trasformata e lo specchio del glb, e saltavano.
                                            const created = sceneEditor.createGroup(parentId, val, meshesToGroup as any, true);

                                            if (!created) {
                                                showErrorNotification({ message: T._('The group could not be created.', 'Admin') });
                                                closeDialog('group-dialog');
                                                return;
                                            }

                                            setMeshRefreshTrigger(true);
                                            onMeshesUpdated(sceneEditor.getSceneMeshes());
                                            notifySceneEditorUpdates();
                                            closeDialog('group-dialog');
                                        }
                                    }
                                ]}
                            >
                                <FormControl label={T._('Group name', 'Admin')}>
                                    <Input
                                        placeholder={T._('Type the group name...', 'Admin')}
                                        fullWidth
                                        onChange={(e) => {
                                            formData.name = e.target.value;
                                        }}
                                    />
                                </FormControl>
                            </Dialog>
                        );
                    }
                },
                {
                    name: T._('Clone', 'Admin'),
                    tooltip: T._('A copy in this scene', 'Admin'),
                    icon: <CloneIcon />,
                    kind: 'action',
                    onClick: () => {
                        treeState.closeOverlay();

                        // Raccogli i nodi da clonare
                        const nodesToClone = ids.map((id) => findNodeById(id)).filter(Boolean);

                        // Clona prima le mesh di Babylon
                        const clonedMeshes = new Map<string, any>();
                        nodesToClone.forEach((node) => {
                            if (node && node.data && sceneEditor) {
                                try {
                                    const clonedMesh = sceneEditor.cloneMesh(node.data.id || node.data.uniqueId);
                                    clonedMeshes.set(node.id, clonedMesh);
                                } catch (error) {
                                    console.error(`Errore clonando mesh per nodo ${node.name}:`, error);
                                }
                            }
                        });

                        // Ora clona i nodi dell'albero e assegna le mesh clonate
                        cloneNodes(ids, 'Copy', (node: SceneObjectNode) => clonedMeshes.get(node.id));

                        // Dopo la clonazione, aggiorna la lista delle mesh per includere quelle nuove
                        if (sceneEditor) {
                            setMeshRefreshTrigger(true);
                            const updatedMeshes = sceneEditor.getSceneMeshes();
                            onMeshesUpdated(updatedMeshes);
                        }

                        // Notifica gli aggiornamenti una sola volta: le copie portano i loro materiali.
                        notifyMaterialUsageChanged();
                        notifySceneEditorUpdates();
                    }
                }
            ];

            commands.push({
                name: T._('Remove', 'Admin'),
                tooltip: T._('Out of this scene', 'Admin'),
                icon: <RemoveIcon />,
                kind: 'danger',
                onClick: () => {
                    // IL RAMO CONTA NELLA DOMANDA: togliere un Empty porta via anche i suoi, e chi
                    // conferma deve saperlo PRIMA — non dopo, guardando l'albero più corto.
                    const branchSize = (node: any): number => ((node?.children ?? []) as any[]).reduce((sum, child) => sum + 1 + branchSize(child), 0);

                    // Chi è già dentro un ramo preso non si nomina due volte.
                    const isUnderSelection = (id: string) => {
                        let parent = treeState.findParentId(id);
                        while (parent) {
                            if (ids.includes(parent)) return true;
                            parent = treeState.findParentId(parent);
                        }
                        return false;
                    };

                    const tops = ids
                        .filter((id) => !isUnderSelection(id))
                        .map((id) => findNodeById(id))
                        .filter((node): node is NonNullable<typeof node> => node != null);

                    askRemove({
                        names: tops.map((node) => node.name),
                        one: T._('object', 'Admin'),
                        many: T._('objects', 'Admin'),
                        inside: tops.reduce((sum, node) => sum + branchSize(node), 0),
                        // Il pannello si chiude solo se si conferma: chi rinuncia lo ritrova com'era.
                        onConfirm: () => {
                            treeState.closeOverlay();

                            // Fai dispose delle mesh prima di rimuovere i nodi
                            ids.forEach((id) => {
                                const node = findNodeById(id);

                                if (node && node.data && typeof node.data.dispose === 'function') {
                                    try {
                                        node.data.dispose();
                                    } catch (error) {
                                        console.error('Error disposing mesh:', error);
                                    }
                                }
                            });

                            removeNodes(ids);
                            onMeshesUpdated(sceneEditor.getSceneMeshes());
                            notifyMaterialUsageChanged();
                        }
                    });
                }
            });

            return commands;
        },
        [
            treeState,
            showDialog,
            closeDialog,
            cloneNodes,
            findNodeById,
            sceneEditor,
            setMeshRefreshTrigger,
            onMeshesUpdated,
            notifySceneEditorUpdates,
            updateNode,
            removeNodes,
            askRemove
        ]
    );

    // I COMANDI SONO FUNZIONI NUOVE A OGNI RENDER, e l'effetto qui sotto non può dipenderne:
    // gli basterebbe per ripartire ogni volta, mandando all'Inspector un elemento nuovo che rende
    // di nuovo l'editor, che rifà i comandi. Il giro non si ferma più. L'effetto guarda quindi la
    // SELEZIONE — che cambia solo quando cambia davvero — e i comandi li legge da qui, sempre
    // freschi perché il riferimento si aggiorna a ogni render.
    const subjectCommandsRef = useRef(subjectCommands);
    subjectCommandsRef.current = subjectCommands;

    // TOGLIERE LA SCELTA NELL'ELENCO È TOGLIERLA ANCHE IN SCENA. Il clic sul vuoto sotto le righe
    // libera l'elenco, ma sul palco la mesh restava scelta col gizmo ancora attaccato: due risposte
    // diverse alla stessa domanda. Il manico lo stacca solo `selectUnselectMesh(…, false)`, perché
    // `unselectAllMeshes` spegne il contorno e lascia dov'è il nodo manipolato.
    //
    // Si guarda il PASSAGGIO da «qualcosa» a «niente», non lo stato: al montaggio la selezione è
    // vuota, e disfare quella fatta da un'altra sezione non è compito di chi apre questa.
    const lastSelection = useRef<string[]>([]);

    useEffect(() => {
        const previous = lastSelection.current;
        lastSelection.current = selectedIds;

        if (!previous.length || selectedIds.length) return;

        // L'elenco ragiona per id di riga, il motore per id di mesh: si traduce finché la riga
        // esiste ancora — se è stata cancellata, la selezione va tolta lo stesso.
        const meshId = findNodeById(previous[0])?.data?.id;

        sceneEditor?.selectUnselectMesh?.(meshId ?? previous[0], false);
    }, [selectedIds, findNodeById, sceneEditor]);

    // IL VUOTO DELLA SCENA NON DESELEZIONA, e non per dimenticanza: ci abbiamo provato e l'abbiamo
    // tolto. Sul palco «il clic non ha preso niente» è una risposta che nessuno sa dare con certezza
    // — il motore avvisa solo quando PRENDE qualcosa — e ogni modo di dedurlo da fuori sbagliava in
    // un caso o nell'altro, deselezionando mesh appena scelte. Chi volesse riprovarci sappia che il
    // punto debole non è dove si aggancia l'ascolto, è la domanda stessa: serve che sia il motore a
    // dire «vuoto», non noi a dedurlo. Il vuoto dell'Outliner invece è vuoto per davvero, ed è lì
    // che la deselezione vive (`SectionPanel.onEmptySpaceClick` → `TreeHandle.clearSelection`).

    // CON PIÙ DI UNA RIGA PRESA, L'INSPECTOR GUARDA IL MUCCHIO. L'albero passa il pannello della
    // riga su cui è caduto il clic, e con sette righe scelte quel pannello descriveva una delle
    // sette come se le altre non ci fossero. Qui la selezione ha la precedenza: è la sola che sa
    // di essere al plurale.
    // E QUANDO IL MUCCHIO SI SCIOGLIE la colonna va liberata, o resta a descrivere una selezione che
    // non c'è più — che è il modo in cui «ho deselezionato tutto» continuava a somigliare a «è ancora
    // selezionato». La libera solo chi l'aveva scritta: quella colonna è una per tutte le sezioni.
    const publishedSelection = useRef(false);

    useEffect(() => {
        if (!onInspectorChange) return;

        if (selectedIds.length < 2) {
            if (publishedSelection.current && selectedIds.length === 0) {
                publishedSelection.current = false;
                onInspectorChange(null);
            }
            return;
        }

        publishedSelection.current = true;

        const entries = selectedIds
            .map((id) => findNodeById(id))
            .filter((node): node is NonNullable<typeof node> => node != null)
            .map((node) => ({
                id: node.id,
                name: node.name,
                icon: node.children && node.children.length > 0 ? <EmptyNodeIcon /> : <ObjectsIcon />,
                // Una voce del badge prende quella sola, come un clic sulla sua riga: in scena e nell'elenco.
                onPick: () => {
                    node.onNodeClick?.();
                    treeRef.current?.selectAndOpenOverlay(node.id);
                }
            }));

        onInspectorChange(<SelectionInspector entries={entries} actions={subjectCommandsRef.current(selectedIds)} icon={<ObjectsRailIcon />} tone='object' />);
    }, [selectedIds, findNodeById, onInspectorChange, treeRef]);

    return (
        <SectionPanel
            label={T._('Scene objects', 'Admin')}
            actions={tools}
            selectionHint={selectableIds.length > 1}
            filter={filter}
            onFilterChange={setFilter}
            searchPlaceholder={T._('Search objects…', 'Admin')}
            onEmptySpaceClick={() => treeRef?.current?.clearSelection?.()}
        >
            <TreeComponent
                ref={treeRef}
                highlight={filter}
                blinkRequest={blinkRequest}
                treeState={treeStateForComponent}
                expandAll={!!filter}
                treeLabel='Scene objects'
                emptyIcon={<ObjectsIcon />}
                emptyMessage={filter.trim() ? T._('No object matches the search.', 'Admin') : T._('The scene holds no objects yet. Add one to start.', 'Admin')}
                showVisibilityIcon={true}
                disableOverlay={!!onInspectorChange}
                onSelectedComponentChange={onInspectorChange}
                /* IL NOME SI CAMBIA DOVE SI LEGGE: si sceglie la riga, e un secondo clic sul nome lo apre.
                   Era una voce nel «…» che apriva una finestra — una porta in più per la stessa stanza, e
                   il mockup quella voce non ce l'ha. IL NOME STA IN DUE POSTI, il nodo e la mesh del
                   motore, e vanno scritti tutti e due o il primo ridisegno riporta su quello di prima. */
                onNodeRename={(node, name) => {
                    updateNode(node.id, { name });

                    const renamed = findNodeById(node.id);
                    if (renamed?.data) renamed.data.name = name;
                }}
                rowActions={(_node, ids) => subjectCommands(ids)}
                onNodeMoved={(nodeId, targetParentId, index) => {
                    const nodesToMove = treeState.selectedIds.has(nodeId) ? Array.from(treeState.selectedIds) : [nodeId];

                    // Aggiorna la gerarchia in Babylon
                    nodesToMove.forEach((id) => {
                        const node = findNodeById(id);
                        if (node && node.data) {
                            const targetNode = targetParentId ? findNodeById(targetParentId) : null;
                            const newParent = targetNode?.data || null; // Usa data anche per il target

                            // Aggiorna il parent nella mesh di Babylon
                            if (node.data.setParent) {
                                node.data.setParent(newParent);
                            } else if (node.data.parent !== undefined) {
                                node.data.parent = newParent;
                            }
                        }
                    });

                    // LO SPOSTAMENTO NELL'ALBERO L'HA GIÀ FATTO CHI HA LASCIATO LA RIGA: qui resta
                    // solo la parte che l'albero non sa fare, cioè riparentare la mesh in Babylon.
                    // E DIRLO (2026-10-15): il nuovo padre si salva con la scena, ma la testata restava su
                    // «Saved» e niente invitava a salvare. Ridisegna e segnala la modifica, come Group e Clone.
                    notifySceneEditorUpdates();
                }}
                onToggleNodeVisibility={handleToggleNodeVisibility}
                showLockIcon
                onToggleNodeLock={handleToggleNodeLock}
            />
        </SectionPanel>
    );
};

export default ObjectEditor;
